/**
 * Endoscopy worklist — the day's procedures, joined onto patient
 * demographics. Driven from the `orders` table; populated upstream
 * by HL7 ORM-O01 intake from the hospital HIS, with status
 * transitions flowing in from MWL C-FIND / MPPS on each modality.
 *
 * Read-only at this surface. Admin (scheduler / receptionist) and
 * clinical readers (owner / senior / junior) all see it; the
 * server-side endpoint /viewer-api/worklist gates by role.
 *
 * Forked from a pathology-shaped queue UI during the modality split
 * (2026-04-25). The original lives on at pathopod/viewer/src/app/
 * WorklistPage.tsx and is unaffected.
 *
 * Issue #5 (2026-05-20) — refit on the shared DataTable: paginator,
 * sortable columns, per-user column-visibility prefs. Default
 * visible columns drop Procedure + Modality and add Videos + Images.
 */
import { For, Show, createSignal, createMemo } from "solid-js";
import { createQuery, useQueryClient } from "@tanstack/solid-query";
import { useNavigate } from "@solidjs/router";
import { EmptyState } from "../components/EmptyState";
import { DataTable } from "../components/DataTable";
import type { DataColumn } from "../components/DataTable";
import { caseId } from "./caseId";
import { fetchStudySources, sourceLabel } from "./studySources";
import { brandedAlert } from "./branded-dialog";
import { auth } from "../auth/store";

interface WorklistRow {
  accession_no:     string;
  mrn:              string;
  patient_name:     string | null;
  patient_sex:      string | null;
  birth_date:       string | null;
  procedure_code:   string;
  procedure_text:   string | null;
  modality:         string | null;
  scheduled_ae:     string | null;
  scheduled_at:     string | null;
  status:           "scheduled" | "in_progress" | "completed" | "cancelled" | "discontinued" | "ad_hoc";
  placer_order_no:  string | null;
  filler_order_no:  string | null;
  assigned_to_sub:  string | null;
  assigned_to_name: string | null;
  assigned_at:      string | null;
  assigned_by_sub:  string | null;
  created_at:       string;
  updated_at:       string;
  study_uid:        string | null;
  ad_hoc:           boolean;
  video_count:      number;
  image_count:      number;
}

interface AdminUser {
  id:        string;
  userName:  string;
  firstName: string;
  lastName:  string;
  email:     string;
  state:     string;
  roles:     string[];
}

interface WorklistCounts {
  total:        number;
  scheduled:    number;
  in_progress:  number;
  completed:    number;
  cancelled:    number;
  discontinued: number;
  ad_hoc:       number;
}

interface WorklistResponse {
  rows:   WorklistRow[];
  counts: WorklistCounts;
}

function authHeader(): Record<string, string> {
  const a = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
  return { Authorization: `Bearer ${a?.accessToken ?? ""}` };
}

async function fetchWorklist(filter: string): Promise<WorklistResponse> {
  const qs = new URLSearchParams();
  if (filter && filter !== "all") qs.set("status", filter);
  // The server-side cap stays at 1000; client-side pagination via
  // DataTable keeps the rendered slice small. The status tabs make
  // the load on a single fetch acceptable for the pilot volumes.
  qs.set("limit", "1000");
  const rsp = await fetch(`/viewer-api/worklist?${qs.toString()}`, { headers: authHeader() });
  if (!rsp.ok) throw new Error(`HTTP ${rsp.status}`);
  return rsp.json();
}

async function fetchReaders(): Promise<AdminUser[]> {
  const rsp = await fetch("/viewer-api/admin/users", { headers: authHeader() });
  if (!rsp.ok) throw new Error(`HTTP ${rsp.status}`);
  const all: AdminUser[] = await rsp.json();
  return all.filter((u) =>
    u.state === "active" &&
    u.roles.some((r) => r === "senior" || r === "junior"),
  );
}

async function postAssign(accession: string, reader_sub: string, reader_name: string): Promise<void> {
  const rsp = await fetch(`/viewer-api/orders/${encodeURIComponent(accession)}/assign`, {
    method:  "POST",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body:    JSON.stringify({ reader_sub, reader_name }),
  });
  if (!rsp.ok) throw new Error(`HTTP ${rsp.status}: ${await rsp.text()}`);
}

async function postUnassign(accession: string): Promise<void> {
  const rsp = await fetch(`/viewer-api/orders/${encodeURIComponent(accession)}/unassign`, {
    method: "POST", headers: authHeader(),
  });
  if (!rsp.ok) throw new Error(`HTTP ${rsp.status}: ${await rsp.text()}`);
}

function fmtTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  if (isToday) return `${hh}:${mm}`;
  const yyyy = d.getFullYear();
  const mo   = String(d.getMonth() + 1).padStart(2, "0");
  const dd   = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mo}-${dd} ${hh}:${mm}`;
}

function fmtAge(iso: string | null): string {
  if (!iso) return "—";
  const ms = new Date(iso).getTime() - Date.now();
  const abs = Math.abs(ms);
  const m = Math.round(abs / 60_000);
  if (m < 60) return ms >= 0 ? `in ${m}m` : `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return ms >= 0 ? `in ${h}h` : `${h}h ago`;
  const d = Math.round(h / 24);
  return ms >= 0 ? `in ${d}d` : `${d}d ago`;
}

function StatusChip(p: { s: WorklistRow["status"] }) {
  const cls =
      p.s === "scheduled"    ? "bg-brand/20 text-brand"
    : p.s === "in_progress"  ? "bg-warn/30 text-warn"
    : p.s === "completed"    ? "bg-ok/20 text-ok"
    : p.s === "cancelled"    ? "bg-ink-200 text-ink-700"
    : p.s === "discontinued" ? "bg-danger/20 text-danger"
    : p.s === "ad_hoc"       ? "bg-info/20 text-info ring-1 ring-info"
    :                          "bg-ink-200 text-ink-700";
  return <span class={`rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${cls}`}>{p.s.replace("_"," ")}</span>;
}

function ModalityChip(p: { m: string | null }) {
  if (!p.m) return null;
  return <span class="rounded bg-ink-200 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-ink-900">{p.m}</span>;
}

type StatusFilter = "all" | WorklistRow["status"];

export function WorklistPage() {
  const nav = useNavigate();
  const [filter, setFilter] = createSignal<StatusFilter>("all");
  const [q, setQ] = createSignal("");

  const data = createQuery(() => ({
    queryKey: ["endo-worklist", filter()],
    queryFn:  () => fetchWorklist(filter()),
    refetchInterval: 30_000,
  }));

  const filtered = createMemo<WorklistRow[]>(() => {
    const rows = data.data?.rows ?? [];
    const needle = q().trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => {
      const hay = [caseId(r.study_uid), r.accession_no, r.mrn, r.patient_name, r.procedure_code, r.procedure_text, r.scheduled_ae]
        .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(needle);
    });
  });

  // Which EndoEdge box uploaded each study (resolved to site name).
  const allUids = createMemo(() =>
    (data.data?.rows ?? []).map((r) => r.study_uid).filter((u): u is string => !!u));
  const sources = createQuery(() => ({
    queryKey: ["study-sources", allUids().join(",")],
    queryFn:  () => fetchStudySources(allUids()),
    enabled:  allUids().length > 0,
    staleTime: 60_000,
  }));
  function sourceFor(uid: string | null): string {
    return sourceLabel(uid ? sources.data?.[uid] : undefined);
  }

  const isAdminOnly = createMemo(() => {
    const r = new Set(auth()?.roles ?? []);
    return r.has("admin") && !r.has("owner") && !r.has("senior") && !r.has("junior");
  });
  const canAssign = createMemo(() => {
    const r = new Set(auth()?.roles ?? []);
    return r.has("admin") || r.has("owner") || r.has("manager");
  });
  const queryClient = useQueryClient();
  const readers = createQuery(() => ({
    queryKey: ["endo-readers"],
    queryFn:  fetchReaders,
    enabled:  canAssign(),
    staleTime: 60_000,
  }));
  const [busyAccession, setBusyAccession] = createSignal<string | null>(null);
  async function doAssign(r: WorklistRow, reader_sub: string) {
    const u = (readers.data ?? []).find((x) => x.id === reader_sub);
    if (!u) return;
    const fullName = [u.firstName, u.lastName].filter(Boolean).join(" ").trim() || u.userName;
    setBusyAccession(r.accession_no);
    try {
      await postAssign(r.accession_no, reader_sub, fullName);
      await queryClient.invalidateQueries({ queryKey: ["endo-worklist"] });
    } catch (e) { brandedAlert(`Assign failed: ${(e as Error).message}`); }
    finally     { setBusyAccession(null); }
  }
  async function doUnassign(r: WorklistRow) {
    setBusyAccession(r.accession_no);
    try {
      await postUnassign(r.accession_no);
      await queryClient.invalidateQueries({ queryKey: ["endo-worklist"] });
    } catch (e) { brandedAlert(`Unassign failed: ${(e as Error).message}`); }
    finally     { setBusyAccession(null); }
  }

  function openOrder(r: WorklistRow) {
    if (isAdminOnly()) return;
    if (r.ad_hoc && r.study_uid) {
      nav(`/study/${encodeURIComponent(r.study_uid)}`);
      return;
    }
    nav(`/studies?accession=${encodeURIComponent(r.accession_no)}`);
  }

  // Column spec — declared once, not reactive. The shared DataTable
  // takes `defaultVisible: false` for columns the operator should
  // see only after opting in via the column picker. Per issue #5
  // the Worklist hides Procedure + Modality by default and shows
  // Videos + Images instead.
  // Default visible set per issue #5 follow-up: Status, Patient, MRN,
  // Videos, Images, Assigned, When.  Everything else is one click away
  // via the Columns ▾ menu and the choice persists in user_prefs.
  const columns: DataColumn<WorklistRow>[] = [
    {
      id: "case", label: "Case ID",
      render: (r) => <span class="font-mono text-ink-900" title={r.study_uid ?? undefined}>{caseId(r.study_uid)}</span>,
      sortValue: (r) => caseId(r.study_uid),
      alwaysVisible: true,
    },
    {
      id: "status", label: "Status",
      render: (r) => <StatusChip s={r.status} />,
      sortValue: (r) => r.status,
    },
    {
      id: "source", label: "EndoEdge box",
      render: (r) => <span class="text-ink-700">{sourceFor(r.study_uid)}</span>,
      sortValue: (r) => sourceFor(r.study_uid),
      alwaysVisible: true,
    },
    {
      id: "scheduled", label: "Scheduled",
      render: (r) => <span class="font-mono">{fmtTime(r.scheduled_at)}</span>,
      sortValue: (r) => r.scheduled_at ? new Date(r.scheduled_at) : null,
      defaultVisible: false,             // redundant with the When column
    },
    {
      id: "accession", label: "Accession",
      render: (r) => <span class="font-mono">{r.accession_no}</span>,
      sortValue: (r) => r.accession_no.toLowerCase(),
      defaultVisible: false,
    },
    {
      id: "patient", label: "Patient",
      render: (r) => (
        <span>
          {r.patient_name ?? "—"}
          <Show when={r.patient_sex || r.birth_date}>
            <span class="ml-1 text-[10px] text-ink-700">
              {[r.patient_sex, r.birth_date].filter(Boolean).join(" · ")}
            </span>
          </Show>
        </span>
      ),
      sortValue: (r) => (r.patient_name ?? "").toLowerCase(),
      alwaysVisible: true,
    },
    {
      id: "mrn", label: "MRN",
      render: (r) => <span class="font-mono">{r.mrn}</span>,
      sortValue: (r) => r.mrn.toLowerCase(),
    },
    {
      id: "procedure", label: "Procedure",
      render: (r) => (
        <span>
          <span class="font-mono text-ink-700">{r.procedure_code}</span>
          <Show when={r.procedure_text}>
            <span class="ml-2">{r.procedure_text}</span>
          </Show>
        </span>
      ),
      sortValue: (r) => (r.procedure_text ?? r.procedure_code ?? "").toLowerCase(),
      defaultVisible: false,             // hidden by default per issue #5
    },
    {
      id: "modality", label: "Mod",
      render: (r) => <ModalityChip m={r.modality} />,
      sortValue: (r) => r.modality ?? "",
      defaultVisible: false,             // hidden by default per issue #5
    },
    {
      id: "videos", label: "Videos",
      render: (r) => (
        <span class={r.video_count ? "font-mono text-ink-900" : "font-mono text-ink-600"}>
          {r.video_count}
        </span>
      ),
      sortValue: (r) => r.video_count,
      cellClass: "text-right",
      headerClass: "text-right",
    },
    {
      id: "images", label: "Images",
      render: (r) => (
        <span class={r.image_count ? "font-mono text-ink-900" : "font-mono text-ink-600"}>
          {r.image_count}
        </span>
      ),
      sortValue: (r) => r.image_count,
      cellClass: "text-right",
      headerClass: "text-right",
    },
    {
      id: "station", label: "Station",
      render: (r) => <span class="font-mono">{r.scheduled_ae ?? "—"}</span>,
      sortValue: (r) => r.scheduled_ae ?? "",
      defaultVisible: false,
    },
    {
      id: "assigned", label: "Assigned to",
      render: (r) => (
        <AssignCell
          row={r}
          readers={readers.data ?? []}
          canAssign={canAssign()}
          busy={busyAccession() === r.accession_no}
          onAssign={(sub) => doAssign(r, sub)}
          onUnassign={() => doUnassign(r)}
        />
      ),
      sortValue: (r) => (r.assigned_to_name ?? "").toLowerCase(),
    },
    {
      id: "when", label: "When",
      render: (r) => <span class="text-ink-700">{fmtAge(r.scheduled_at)}</span>,
      sortValue: (r) => r.scheduled_at ? new Date(r.scheduled_at) : null,
    },
  ];

  function FilterButton(p: { v: StatusFilter; label: string; count: number }) {
    const active = () => filter() === p.v;
    return (
      <button type="button"
              onClick={() => setFilter(p.v)}
              class="rounded px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider"
              classList={{
                "bg-brand text-white":          active(),
                "bg-ink-200 text-ink-900 hover:bg-ink-300": !active(),
              }}>
        {p.label} <span class="font-mono opacity-80">{p.count}</span>
      </button>
    );
  }

  return (
    <div class="flex h-full flex-col">
      <header class="flex flex-wrap items-center gap-3 border-b border-ink-300 bg-ink-100 px-4 py-3">
        <span class="chip-brand">Worklist</span>
        <span class="text-xs text-ink-700">
          Scheduled procedures · live (refreshes every 30s)
        </span>
        <div class="ml-auto flex items-center gap-2">
          <FilterButton v="all"          label="All"        count={data.data?.counts.total ?? 0} />
          <FilterButton v="scheduled"    label="Scheduled"  count={data.data?.counts.scheduled ?? 0} />
          <FilterButton v="in_progress"  label="In progress" count={data.data?.counts.in_progress ?? 0} />
          <FilterButton v="completed"    label="Completed"  count={data.data?.counts.completed ?? 0} />
          <FilterButton v="ad_hoc"       label="Ad-hoc"     count={data.data?.counts.ad_hoc ?? 0} />
        </div>
      </header>

      <div class="flex flex-wrap items-center gap-3 border-b border-ink-300 bg-ink-100 px-4 py-2">
        <input type="search"
               placeholder="Accession, MRN, patient name, procedure, station…"
               class="min-w-[280px] flex-1 rounded border border-ink-300 bg-ink-200 px-3 py-1.5 text-sm placeholder:text-ink-700 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
               value={q()}
               onInput={(e) => setQ(e.currentTarget.value)} />
        <Show when={data.data}>
          <span class="text-[11px] text-ink-700">
            {filtered().length} of {data.data!.rows.length} order(s)
          </span>
        </Show>
      </div>

      <Show when={data.isError}>
        <div class="m-4 rounded border border-danger bg-danger/10 p-3 text-[11px] text-danger">
          {(data.error as Error).message}
        </div>
      </Show>

      <Show when={data.data && data.data.rows.length === 0}>
        <EmptyState
          title="No orders in the worklist"
          note={
            <>
              Endoscopy orders arrive via HL7 ORM-O01 messages from the
              hospital HIS. Once an order lands here it stays in
              <code class="mx-1 rounded bg-ink-200 px-1 py-0.5 font-mono text-[10px]">scheduled</code>
              until the modality starts the procedure (MPPS), then
              transitions through <code class="mx-1 rounded bg-ink-200 px-1 py-0.5 font-mono text-[10px]">in_progress</code>
              to <code class="mx-1 rounded bg-ink-200 px-1 py-0.5 font-mono text-[10px]">completed</code>.
            </>
          }
        />
      </Show>

      <Show when={data.data && data.data.rows.length > 0}>
        <div class="flex-1 min-h-0 p-3">
          <DataTable<WorklistRow>
            tableId="worklist"
            rows={filtered}
            columns={columns}
            rowKey={(r) => r.accession_no || (r.study_uid ?? "")}
            onRowClick={(r) => openOrder(r)}
            defaultSort={{ col: "scheduled", dir: "desc" }}
            defaultPageSize={50}
            loading={() => data.isLoading}
            emptyState={<span>No orders match the current filters.</span>}
          />
        </div>
      </Show>
    </div>
  );
}

function AssignCell(p: {
  row:        WorklistRow;
  readers:    AdminUser[];
  canAssign:  boolean;
  busy:       boolean;
  onAssign:   (reader_sub: string) => void;
  onUnassign: () => void;
}) {
  if (!p.canAssign) {
    return <span class="text-[11px] text-ink-700">{p.row.assigned_to_name ?? "—"}</span>;
  }
  return (
    <div class="flex items-center gap-1.5"
         onClick={(e) => e.stopPropagation()}>
      <select
        class="rounded border border-ink-300 bg-ink-200 px-1.5 py-0.5 text-[11px] text-ink-1000 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
        disabled={p.busy}
        onChange={(e) => {
          const v = e.currentTarget.value;
          if (v) p.onAssign(v);
        }}
      >
        <option value="" selected={!p.row.assigned_to_sub}>— Unassigned —</option>
        <For each={p.readers}>{(u) => (
          <option value={u.id} selected={p.row.assigned_to_sub === u.id}>
            {[u.firstName, u.lastName].filter(Boolean).join(" ") || u.userName}
            {u.roles.includes("senior") ? "  · senior" : u.roles.includes("junior") ? "  · junior" : ""}
          </option>
        )}</For>
      </select>
      <Show when={p.row.assigned_to_sub}>
        <button type="button"
                class="text-[11px] text-ink-700 hover:text-danger disabled:opacity-50"
                disabled={p.busy}
                title="Unassign"
                onClick={(e) => { e.stopPropagation(); p.onUnassign(); }}>×</button>
      </Show>
    </div>
  );
}
